import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Assignment,
  Timeline,
  CheckCircle,
  Warning,
  ArrowBack,
  Print
} from '@mui/icons-material';
import { Link as MuiLink } from '@mui/material';
import logo from '../assets/logo0.png'; // Import the logo directly

const ReportView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Mock data - In a real application, you would fetch this data based on the ID
  const reportData = {
    overview: {
      totalReports: 156,
      resolutionRate: 79,
      pendingReports: 22,
      criticalIssues: 10
    },
    details: {
      id: 1,
      title: "Server Performance Issue",
      status: "Critical",
      date: "2024-02-20",
      category: "Performance",
      assignee: "John Doe",
      priority: "High"
    },
    actions: {
      initialAssessment: "High CPU usage detected on main application server",
      troubleshootingSteps: [
        "Analyzed server logs",
        "Monitored resource utilization",
        "Identified memory leak in application"
      ],
      currentStatus: "Pending",
      additionalNotes: "Memory leak appears to be related to recent deployment"
    },
    recommendations: {
      shortTerm: [
        "Implement temporary memory cleanup routine",
        "Scale up server resources"
      ],
      longTerm: [
        "Code review of recent changes",
        "Implement automated performance monitoring"
      ]
    },
    actionItems: {
      assignedTeam: "Backend Team",
      deadline: "2024-02-25",
      followUpRequired: "Yes"
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      Critical: 'error',
      High: 'warning',
      Medium: 'info',
      Low: 'success'
    };
    return colors[status] || 'default';
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/reports')}
            sx={{ mb: 2 }}
          >
            Back to Reports
          </Button>
          <Typography variant="h4" gutterBottom>
            Incident Report Summary
          </Typography>
        </Box>

        {/* Report Overview */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            1. Report Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary">Total Reports</Typography>
                  <Typography variant="h4">{reportData.overview.totalReports}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary">Resolution Rate</Typography>
                  <Typography variant="h4">{reportData.overview.resolutionRate}%</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary">Pending Reports</Typography>
                  <Typography variant="h4">{reportData.overview.pendingReports}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary">Critical Issues</Typography>
                  <Typography variant="h4">{reportData.overview.criticalIssues}</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Report Details */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            2. Report Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography><strong>Report ID:</strong> #{reportData.details.id}</Typography>
              <Typography><strong>Title:</strong> {reportData.details.title}</Typography>
              <Typography><strong>Date:</strong> {reportData.details.date}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography>
                <strong>Status:</strong>{' '}
                <Chip
                  label={reportData.details.status}
                  color={getStatusColor(reportData.details.status)}
                  size="small"
                />
              </Typography>
              <Typography><strong>Category:</strong> {reportData.details.category}</Typography>
              <Typography><strong>Assignee:</strong> {reportData.details.assignee}</Typography>
              <Typography>
                <strong>Priority:</strong>{' '}
                <Chip
                  label={reportData.details.priority}
                  color={getStatusColor(reportData.details.priority)}
                  size="small"
                />
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Actions Taken */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            3. Actions Taken
          </Typography>
          <Typography><strong>Initial Assessment:</strong></Typography>
          <Typography paragraph>{reportData.actions.initialAssessment}</Typography>
          
          <Typography><strong>Troubleshooting Steps:</strong></Typography>
          <ul>
            {reportData.actions.troubleshootingSteps.map((step, index) => (
              <li key={index}><Typography>{step}</Typography></li>
            ))}
          </ul>
          
          <Typography><strong>Current Status:</strong> {reportData.actions.currentStatus}</Typography>
          <Typography><strong>Additional Notes:</strong> {reportData.actions.additionalNotes}</Typography>
        </Paper>

        {/* Recommendations */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            4. Recommendations
          </Typography>
          <Typography><strong>Short-Term Fixes:</strong></Typography>
          <ul>
            {reportData.recommendations.shortTerm.map((fix, index) => (
              <li key={index}><Typography>{fix}</Typography></li>
            ))}
          </ul>
          
          <Typography><strong>Long-Term Solutions:</strong></Typography>
          <ul>
            {reportData.recommendations.longTerm.map((solution, index) => (
              <li key={index}><Typography>{solution}</Typography></li>
            ))}
          </ul>
        </Paper>

        {/* Action Items */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            5. Action Items
          </Typography>
          <Typography><strong>Assigned Team Member:</strong> {reportData.actionItems.assignedTeam}</Typography>
          <Typography><strong>Deadline for Resolution:</strong> {reportData.actionItems.deadline}</Typography>
          <Typography><strong>Follow-Up Required:</strong> {reportData.actionItems.followUpRequired}</Typography>
        </Paper>

        {/* Print Button Section */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 4 }}>
          <Button
            variant="contained"
            startIcon={<Print />}
            onClick={handlePrint}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            }}
          >
            Print Report
          </Button>
        </Box>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          width: '100%',
          bgcolor: '#000000',
          color: 'white',
          py: 4, // Increased padding
          mt: 'auto',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          '@media print': {
            display: 'none'
          }
        }}
      >
        <Container maxWidth="lg">
          <Grid 
            container 
            spacing={4}
            alignItems="center"
            justifyContent="space-between"
          >
            {/* Logo Section */}
            <Grid item xs={12} sm={3} sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Box
                component="img"
                src={logo} // Using imported logo
                alt="App Logo"
                sx={{
                  height: 45,
                  width: 'auto',
                  filter: 'brightness(0) invert(1)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)'
                  }
                }}
              />
            </Grid>

            {/* Navigation Links */}
            <Grid item xs={12} sm={9}>
              <Grid 
                container 
                spacing={4}
                justifyContent={{ xs: 'center', sm: 'flex-end' }}
                sx={{ mt: { xs: 2, sm: 0 } }}
              >
                <Grid item>
                  <MuiLink
                    component={RouterLink}
                    to="/dashboard"
                    sx={{
                      color: 'white',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      position: 'relative',
                      '&:after': {
                        content: '""',
                        position: 'absolute',
                        width: '0',
                        height: '2px',
                        bottom: '-4px',
                        left: '0',
                        backgroundColor: 'white',
                        transition: 'width 0.3s ease-in-out'
                      },
                      '&:hover:after': {
                        width: '100%'
                      }
                    }}
                  >
                    Dashboard
                  </MuiLink>
                </Grid>
                <Grid item>
                  <MuiLink
                    component={RouterLink}
                    to="/ai-detect"
                    sx={{
                      color: 'white',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      position: 'relative',
                      '&:after': {
                        content: '""',
                        position: 'absolute',
                        width: '0',
                        height: '2px',
                        bottom: '-4px',
                        left: '0',
                        backgroundColor: 'white',
                        transition: 'width 0.3s ease-in-out'
                      },
                      '&:hover:after': {
                        width: '100%'
                      }
                    }}
                  >
                    AI Detect
                  </MuiLink>
                </Grid>
                <Grid item>
                  <MuiLink
                    component={RouterLink}
                    to="/support"
                    sx={{
                      color: 'white',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      position: 'relative',
                      '&:after': {
                        content: '""',
                        position: 'absolute',
                        width: '0',
                        height: '2px',
                        bottom: '-4px',
                        left: '0',
                        backgroundColor: 'white',
                        transition: 'width 0.3s ease-in-out'
                      },
                      '&:hover:after': {
                        width: '100%'
                      }
                    }}
                  >
                    Support
                  </MuiLink>
                </Grid>
                <Grid item>
                  <MuiLink
                    component={RouterLink}
                    to="/questions"
                    sx={{
                      color: 'white',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      position: 'relative',
                      '&:after': {
                        content: '""',
                        position: 'absolute',
                        width: '0',
                        height: '2px',
                        bottom: '-4px',
                        left: '0',
                        backgroundColor: 'white',
                        transition: 'width 0.3s ease-in-out'
                      },
                      '&:hover:after': {
                        width: '100%'
                      }
                    }}
                  >
                    Questions
                  </MuiLink>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Copyright Section */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              textAlign: 'center',
              fontSize: '0.875rem',
              color: 'rgba(255, 255, 255, 0.7)'
            }}
          >
            © {new Date().getFullYear()} Your Company Name. All rights reserved.
          </Box>
        </Container>
      </Box>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            @page {
              margin: 20mm;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
    </>
  );
};

export default ReportView; 